'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useUiContent } from '@/components/ui-content-provider';
import { createPortal } from 'react-dom';
import { LanguageType } from '@/shared/src/types/languages.type';

type AffiliateInfoIconProps = {
  languageCode: LanguageType;
  /**
   * A second notice shown in full under the disclosure in the tooltip, and
   * read as part of the button's accessible name. The offers panels pass
   * what their prices are (the best price we observed, the store confirms
   * at checkout) and, when they list an Amazon price, Amazon's price
   * disclaimer (docs/OFFERS_ENGINE_V2.md section 6.1.4); the price line under
   * a buy button passes the disclaimer when its price is Amazon's. Absent
   * everywhere else, so every other icon renders exactly as before.
   */
  note?: string | ReadonlyArray<string>;
};

type PopoverPosition = {
  top: number;
  left: number;
  align: 'center' | 'left' | 'right';
};

export function AffiliateInfoIcon({ languageCode, note }: AffiliateInfoIconProps) {
  const notes = (typeof note === 'string' ? [note] : (note ?? [])).filter((text) => text.trim().length > 0);
  const ui = useUiContent();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0, align: 'center' });
  const [mounted, setMounted] = useState(false);
  const iconRef = useRef<HTMLButtonElement>(null);

  const disclosure = ui?.affiliateDisclosure ?? ui.affiliateDisclosure;

  useEffect(() => {
    setMounted(true);
  }, []);

  const computePosition = useCallback(() => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    const POPOVER_WIDTH = 256;
    const GAP = 8;

    const centerX = rect.left + rect.width / 2;
    const viewportWidth = window.innerWidth;

    let left = centerX;
    let align: PopoverPosition['align'] = 'center';

    if (centerX - POPOVER_WIDTH / 2 < 8) {
      left = rect.left;
      align = 'left';
    } else if (centerX + POPOVER_WIDTH / 2 > viewportWidth - 8) {
      left = rect.right;
      align = 'right';
    }

    setPosition({
      top: rect.top - GAP,
      left,
      align,
    });
  }, []);

  const open = useCallback(() => {
    computePosition();
    setIsOpen(true);
  }, [computePosition]);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const onScroll = () => close();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  const translateX =
    position.align === 'center'
      ? '-50%'
      : position.align === 'right'
        ? '-100%'
        : '0%';

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        onMouseEnter={open}
        onMouseLeave={close}
        onClick={(e) => {
          e.stopPropagation();
          isOpen ? close() : open();
        }}
        // The icon is drawn 14 px; the ::before gives it a 24 px hit area (WCAG 2.5.8)
        // without moving the price row. It was a 9 to 14 px target on phones.
        className="relative inline-flex shrink-0 items-center justify-center leading-none text-gray-300 transition-colors duration-200 before:absolute before:-inset-1.5 before:content-[''] hover:text-gray-400 focus:outline-none dark:text-gray-600 dark:hover:text-gray-500"
        // The note is part of the control's name, so a screen reader hears
        // every notice the tooltip shows (no hidden copy of it elsewhere).
        aria-label={[disclosure, ...notes].join(' ')}
        aria-expanded={isOpen}
      >
        <IoInformationCircleOutline className="h-3.5 w-3.5" />
      </button>

      {mounted &&
        isOpen &&
        createPortal(
          <div
            role="tooltip"
            onMouseEnter={open}
            onMouseLeave={close}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              transform: `translateX(${translateX}) translateY(-100%)`,
              zIndex: 99999,
              width: 256,
            }}
            className="pointer-events-auto rounded-2xl border border-gray-200/60 bg-white/90 px-3.5 py-3 text-[0.72rem] leading-relaxed text-gray-500 shadow-[0_8px_32px_rgba(0,0,0,0.10)] backdrop-blur-md animate-in fade-in duration-150 dark:border-hairline dark:bg-card/95 dark:text-muted dark:shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
          >
            {disclosure}
            {notes.map((text) => (
              <p key={text} className="mt-2">
                {text}
              </p>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
